import React from 'react';
import PropTypes from 'prop-types';
import I18n from 'utils/I18n';
import createNetworkingClient from 'utils/networking-client';
import Avatar from '@strava/ui/Avatar';
import Button, { LinkButton } from '@strava/ui/Button';
import { Markup } from 'interweave';
import { commentsProps } from '../../../../props/feed-prop-types';

import styles from './styles.scss';

const prefix = 'components.feed.social';

const CommentsList = ({
  comments,
  onSeeMoreCommentsClick,
  onCommentDelete
}) => {
  const numComments = comments.length;
  const totalComments = Math.min(numComments, 2);

  const deleteComment = (commentId) => {
    createNetworkingClient()
      .delete('/feed/comment', { data: { id: commentId } })
      .then(onCommentDelete(commentId));
  };

  const commentsList = () => {
    const entries = [];
    comments
      .slice(Math.max(numComments - totalComments, 0))
      .forEach((entry) => {
        entries.push(
          <li key={entry.comment_id} className={styles.media}>
            <div className={styles.mediaLeft}>
              <Avatar
                name={entry.athlete.name}
                src={entry.athlete.avatar_url}
                type="athlete"
                size="xsmall"
                data-testid="comment_avatar"
                href={entry.athlete.url}
                badge={entry.athlete.member_type}
              />
            </div>
            <div className={`${styles.comment} ${styles.mediaBody}`}>
              <div className={styles.nameAndTime}>
                <div className={styles.athleteName}>
                  <a href={entry.athlete.url}>{entry.athlete.name}</a>
                </div>

                <div
                  className={styles.timestampAndCommentActions}
                  data-testid="comment-actions"
                >
                  <Markup noWrap={true} content={entry.timestamp} />
                  {(entry.deletable || entry.reportable) && (
                    <div className={styles.commentActions}>
                      {entry.reportable && (
                        <LinkButton
                          variant="text"
                          data-testid="report-comment-btn"
                          className={styles.reportAndDeleteButtons}
                          href={`/comments/${entry.comment_id}/feedback`}
                        >
                          {I18n.t(`${prefix}.report`)}
                        </LinkButton>
                      )}
                      {entry.deletable && (
                        <Button
                          className={styles.reportAndDeleteButtons}
                          variant="text"
                          data-testid="delete-comment-btn"
                          onClick={() => deleteComment(entry.comment_id)}
                        >
                          {I18n.t(`${prefix}.delete`)}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <span className={styles.commentText}>
                <Markup noWrap={true} content={entry.comment} />
              </span>
            </div>
          </li>
        );
      });
    return entries;
  };

  return (
    <div className={styles.comments}>
      {numComments > 0 && <ol>{commentsList()}</ol>}
      {numComments > 2 && (
        <Button
          data-testid="see_all_comments_btn"
          className={styles.moreCommentsButton}
          variant="text"
          onClick={onSeeMoreCommentsClick}
        >
          {I18n.t(`${prefix}.see_all_comments`, {
            number: numComments
          })}
        </Button>
      )}
    </div>
  );
};

CommentsList.propTypes = {
  comments: commentsProps.isRequired,
  onSeeMoreCommentsClick: PropTypes.func.isRequired,
  onCommentDelete: PropTypes.func.isRequired
};

export default CommentsList;
